import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, ShieldAlert, RefreshCw } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-acid text-ink-900",
  contacted: "border border-white/30 text-white",
  closed: "border border-white/15 text-white/40",
};

const NEXT_STATUS = { new: "contacted", contacted: "closed", closed: "new" };

export default function Admin({ user }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`, { withCredentials: true });
      setQuotes(res.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("This account is not authorized for admin access.");
      } else {
        toast.error("Could not load quotes.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/", { replace: true });
  };

  const cycleStatus = async (q) => {
    const next = NEXT_STATUS[q.status] || "new";
    try {
      await axios.patch(`${API}/quotes/${q.id}`, { status: next }, { withCredentials: true });
      setQuotes((prev) => prev.map((x) => (x.id === q.id ? { ...x, status: next } : x)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const counts = useMemo(() => {
    const c = { all: quotes.length, new: 0, contacted: 0, closed: 0 };
    quotes.forEach((q) => (c[q.status] = (c[q.status] || 0) + 1));
    return c;
  }, [quotes]);

  const visible = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  if (user && user.is_admin === false) {
    return (
      <div className="min-h-screen bg-ink-900 text-white grid place-items-center px-5" data-testid="admin-forbidden">
        <div className="text-center max-w-md">
          <ShieldAlert className="mx-auto text-acid" size={40} />
          <h1 className="mt-5 font-display font-black text-3xl tracking-tighter">NOT AUTHORIZED</h1>
          <p className="mt-3 text-white/55">
            You're signed in as {user.email}, which isn't on the admin list. Ask the owner to add your email.
          </p>
          <button onClick={logout} className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-6 py-3 hover:bg-acid hover:text-ink-900 transition-colors">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-900 text-white" data-testid="admin-page">
      <div className="noise-overlay" aria-hidden />
      <header className="border-b border-line sticky top-0 bg-ink-900/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display font-black text-xl tracking-tighter">{SITE.brand}</span>
            <span className="h-2 w-2 bg-acid" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hidden sm:inline">Leads Inbox</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="grid place-items-center h-10 w-10 border border-line hover:bg-ink-800 transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-4 py-2.5 hover:bg-acid hover:text-ink-900 hover:border-acid transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-acid mb-3">Dashboard</p>
            <h1 className="font-display font-black tracking-tighter text-5xl md:text-6xl leading-none">
              QUOTE REQUESTS
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", "new", "contacted", "closed"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${
                  filter === f ? "bg-acid text-ink-900 border-acid" : "border-line text-white/60 hover:text-white"
                }`}
              >
                {f} · {counts[f] ?? 0}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-32 text-center text-white/40 font-mono text-xs uppercase tracking-[0.2em]">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-32 text-center border border-line" data-testid="admin-empty">
            <Inbox className="mx-auto text-white/30" size={40} />
            <p className="mt-4 text-white/50">No {filter !== "all" ? filter : ""} requests yet.</p>
          </div>
        ) : (
          <div className="border border-line divide-y divide-line" data-testid="quotes-list">
            {visible.map((q) => (
              <div key={q.id} data-testid={`quote-row-${q.id}`} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-5 md:p-6 hover:bg-ink-800 transition-colors">
                <div className="lg:col-span-3">
                  <p className="font-display font-bold text-xl tracking-tight">{q.name}</p>
                  <p className="text-white/45 text-sm">{q.company || "—"}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/35">
                    {new Date(q.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="lg:col-span-3 text-sm">
                  <a href={`mailto:${q.email}`} className="block text-white/80 hover:text-acid transition-colors break-all">{q.email}</a>
                  <a href={`tel:${q.phone}`} className="block text-white/60 hover:text-acid transition-colors">{q.phone}</a>
                </div>
                <div className="lg:col-span-4">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-widest bg-ink-700 px-2 py-1 mb-2">{q.service}</span>
                  {q.facility_size ? <p className="text-white/50 text-xs mb-1">Size: {q.facility_size} sq ft</p> : null}
                  {q.message ? <p className="text-white/70 text-sm leading-relaxed">{q.message}</p> : <p className="text-white/30 text-sm">No message</p>}
                </div>
                <div className="lg:col-span-2 lg:text-right">
                  <button
                    data-testid={`status-${q.id}`}
                    onClick={() => cycleStatus(q)}
                    title="Click to change status"
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`}
                  >
                    {q.status}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
